// src/Redux/slices/userSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as interFace from "../../Config/interface.config";
import * as PATH from "../../API/path";
import APIRequest from "../../API";
import toast from "react-hot-toast";
import { logout } from "./authSlice";

// Initial state for logged-in user
const initialState: Partial<interFace.UserDetails> = {
  loading: false,
  totalPost: 0,
};

const freshUserState = (payload?: Partial<interFace.UserDetails>) => ({
  ...initialState,
  ...(payload ?? {}),
  loading: false,
});

export const getUserDetails = createAsyncThunk("/user/details", async () => {
  try {
    const { data }: any = await APIRequest(PATH.userDetails);
    return data;
  } catch (error) {
    throw error;
  }
});

export const updateProfile = createAsyncThunk<any, interFace.UserProfile>(
  "/user/update-profile",
  async (user, { rejectWithValue }) => {
    try {
      delete user.email;
      delete user.id;

      toast.loading("Updating profile...");

      const { data }: any = await APIRequest(PATH.updateUser, user);

      toast.remove();
      toast.success("Profile updated successfully");

      return data;
    } catch (error: any) {
      toast.remove();
      toast.error(error?.response?.data?.message ?? "Update failed");

      return rejectWithValue({
        reason: error?.message ?? error?.response?.data,
      });
    }
  }
);

type SetProfileImagePayload = {
  imageUrl?: string;
  backgroundImageUrl?: string;
};

type SetMembershipPricesPayload = Pick<
  interFace.MemberDetails,
  "monthlyPrice" | "messagePrice"
>;

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateUser(_state, { payload }) {
      return freshUserState(payload);
    },
    setProfileImage(state, { payload }: PayloadAction<SetProfileImagePayload>) {
      if (payload.imageUrl && state.image) {
        state.image.fileURL = payload.imageUrl;
      }
      if (payload.backgroundImageUrl && state.backgroundImage) {
        state.backgroundImage.fileURL = payload.backgroundImageUrl;
      }
    },
    setUserRole(state, { payload }) {
      state.role = payload;
    },
    setMembershipPrices(
      state,
      { payload }: PayloadAction<SetMembershipPricesPayload>,
    ) {
      state.memberDetails = {
        ...state.memberDetails,
        ...payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserDetails.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getUserDetails.fulfilled, (_state, { payload }) => {
      return freshUserState(payload);
    });

    builder.addCase(getUserDetails.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateProfile.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(updateProfile.fulfilled, (_state, { payload }) => {
      return freshUserState(payload);
    });

    builder.addCase(updateProfile.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(logout, () => freshUserState());
  },
});

export const { updateUser, setProfileImage, setUserRole, setMembershipPrices } =
  userSlice.actions;

export default userSlice.reducer;